import React from "react";
import { HoverCard, ScrollArea, Stack, Text } from "@mantine/core";
import { getAttendeeEmails } from "./meetingDistUtils";

// The guest list is something people read, and sometimes copy out of, so it
// needs a surface the mouse can move into. A tooltip closes the moment the
// pointer leaves the number, which made a long list impossible to reach.
const AttendeeCount = ({ attendees }) => {
  const count = attendees?.length || 0;
  const emails = getAttendeeEmails(attendees);
  // Google can return an attendee with a display name and no address, so the
  // list can be shorter than the count. Say so rather than letting the header
  // promise names the card does not have.
  const withoutEmail = Math.max(0, count - emails.length);

  if (!emails.length) {
    return (
      <Text size="sm" weight={500}>
        {count}
      </Text>
    );
  }

  return (
    <HoverCard
      // The table sits inside a Paper with overflow:hidden and a scroll area,
      // so anything rendered inline is clipped near either edge.
      withinPortal
      width={260}
      shadow="md"
      radius="md"
      position="bottom"
      openDelay={100}
      // Enough to cross the gap between the number and the card.
      closeDelay={120}
    >
      <HoverCard.Target>
        <Text
          size="sm"
          weight={500}
          // The row around this is a role="button" that opens the meeting, so
          // the count needs its own stop on the tab order — Mantine opens the
          // card on focus once the target is focusable.
          tabIndex={0}
          role="button"
          aria-label={`Show ${count} ${count === 1 ? "attendee" : "attendees"}`}
          onKeyDown={(event) => event.stopPropagation()}
          sx={{ cursor: "help", display: "inline-block" }}
        >
          {count}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown
        p="xs"
        // The dropdown is portaled to the body, but React still bubbles its
        // events through the tree — so a click, or the end of a drag-select,
        // reached the row and opened the meeting.
        onClick={(event) => event.stopPropagation()}
      >
        <Text
          size="xs"
          weight={700}
          color="dimmed"
          transform="uppercase"
          mb={6}
          sx={{ letterSpacing: "0.5px" }}
        >
          {count} {count === 1 ? "attendee" : "attendees"}
        </Text>
        {/* Every guest is listed — the card scrolls rather than truncating. */}
        <ScrollArea.Autosize maxHeight={220} type="auto">
          <Stack spacing={2}>
            {emails.map((email, index) => (
              <Text
                key={`${email}-${index}`}
                size="xs"
                sx={{ wordBreak: "break-all" }}
              >
                {email}
              </Text>
            ))}
          </Stack>
        </ScrollArea.Autosize>
        {withoutEmail > 0 && (
          <Text size="xs" color="dimmed" mt={6}>
            {withoutEmail} with no email address
          </Text>
        )}
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default AttendeeCount;
